import React, { useState, useEffect } from 'react';
import {
  X, Clock, MapPin, Users, ChevronDown, ChevronUp,
  Check, CalendarPlus, Utensils, Briefcase, Coffee,
  BookOpen, PartyPopper, Gamepad2,
} from 'lucide-react';
import { MapModal } from '../mobile/EventDetailModal';
import ScheduleDetailModal from '../chat/ScheduleDetailModal';
import { auth } from '../../config/firebaseConfig';
import DGPredictionBadge from './DGPredictionBadge';
import { API_BASE_URL } from '../../config/apiConfig';


// ─── 상수 ───────────────────────────────────────────────
const PURPOSE_MAP = {
  meal:    { label: '식사',   icon: Utensils,     color: 'text-orange-500' },
  work:    { label: '업무',   icon: Briefcase,    color: 'text-blue-500'   },
  social:  { label: '모임',   icon: Coffee,       color: 'text-pink-500'   },
  study:   { label: '학습',   icon: BookOpen,     color: 'text-green-500'  },
  event:   { label: '이벤트', icon: PartyPopper,  color: 'text-yellow-500' },
  leisure: { label: '여가',   icon: Gamepad2,     color: 'text-indigo-500' },
};

const ACTION_LABELS = {
  PROPOSE_TIME:   '시간 제안',
  OPPOSE_TIME:    '시간 반대',
  MODIFY_TIME:    '시간 수정',
  PROPOSE_PLACE:  '장소 제안',
  OPPOSE_PLACE:   '장소 반대',
  MODIFY_PLACE:   '장소 수정',
  ADD_PERSON:     '참여자 추가',
  REMOVE_PERSON:  '참여자 제거',
  CONFIRM:        '확정',
  CANCEL:         '취소',
  PASSIVE_ACCEPT: '수락',
};

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

// ─── 미정 카드 ────────────────────────────────────────────
const UndecidedCard = ({ isSelected, onSelect }) => (
  <button
    onClick={onSelect}
    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all text-left
      ${isSelected ? 'bg-blue-50' : 'bg-white'}`}
    style={isSelected
      ? { border: '2px dashed #60a5fa' }
      : { border: '1px dashed #cbd5e1', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
  >
    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all
      ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}
    >
      {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
    </div>
    <span className={`text-sm font-medium ${isSelected ? 'text-blue-600' : 'text-gray-400'}`}>
      미정
    </span>
  </button>
);

// ─── 시간 후보 카드 ──────────────────────────────────────
const TimeCandidateCard = ({ candidate, isSelected, onSelect }) => {
  const attrs = candidate.attributes || candidate;
  const isCurrent = candidate.isCurrent;
  const dow = attrs.dayOfWeek != null ? `(${DAY_NAMES[attrs.dayOfWeek % 7]})` : '';

  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all text-left
        ${isSelected
          ? 'bg-blue-50'
          : isCurrent
            ? 'bg-amber-50'
            : 'bg-white'
        }`}
      style={isSelected
        ? { border: '2px solid #3b82f6' }
        : isCurrent
          ? { border: '2px solid #fcd34d' }
          : { border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      {/* 선택 인디케이터 */}
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all
        ${isSelected ? 'border-blue-500 bg-blue-500' : isCurrent ? 'border-amber-400' : 'border-gray-300'}`}
      >
        {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
      </div>

      {/* 내용 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-semibold ${isSelected ? 'text-blue-700' : isCurrent ? 'text-amber-700' : 'text-gray-800'}`}>
            {attrs.date} {dow}
          </span>
          {isCurrent && (
            <span style={{ backgroundColor: '#f59e0b', color: '#ffffff', fontSize: '10px', padding: '2px 8px', borderRadius: '999px', fontWeight: '700', border: '1px solid #d97706', flexShrink: 0 }}>
              현재
            </span>
          )}
        </div>
        <div className={`text-xs mt-0.5 ${isSelected ? 'text-blue-500' : isCurrent ? 'text-amber-500' : 'text-gray-400'}`}>
          {attrs.startTime} ~ {attrs.endTime}
        </div>
        {/* 수락확률 배지 (A4-03) */}
        <DGPredictionBadge predictions={candidate.dgPredictions} score={candidate.dgScore} />
      </div>
    </button>
  );
};

// ─── 장소 후보 카드 ──────────────────────────────────────
const DG_ROLE_LABEL = {
  highest_accept:     { text: '수락률 최고', color: '#2563eb', bg: '#dbeafe' },
  post_modify_best:   { text: '수정 시 최적', color: '#7c3aed', bg: '#ede9fe' },
  expand_alternative: { text: '새로운 선택', color: '#0d9488', bg: '#ccfbf1' },
};

const PlaceCandidateCard = ({ candidate, isSelected, onSelect, onShowMap }) => {
  const attrs = candidate.attributes || candidate;
  const isCurrent = candidate.isCurrent;
  const roleInfo = candidate.dgRole ? DG_ROLE_LABEL[candidate.dgRole] : null;
  // selfTravelMinutes: 나의 이동시간 (서버에서 계산). 없으면 minTravelMinutes 폴백
  const selfTravelMinutes = candidate.scores?.selfTravelMinutes;
  const minTravelMinutes = candidate.scores?.minTravelMinutes;
  const displayTravelMinutes = selfTravelMinutes ?? minTravelMinutes;
  const coords = attrs.coordinates || candidate.coordinates;
  const hasLocation = !!(coords?.lat || attrs.address);

  const handleLocationClick = (e) => {
    e.stopPropagation();
    onShowMap?.({
      address: attrs.name || attrs.address || '장소',
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
    });
  };

  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all text-left
        ${isSelected
          ? 'bg-blue-50'
          : isCurrent
            ? 'bg-amber-50'
            : 'bg-white'
        }`}
      style={isSelected
        ? { border: '2px solid #3b82f6' }
        : isCurrent
          ? { border: '2px solid #fcd34d' }
          : { border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      {/* 선택 인디케이터 */}
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all
        ${isSelected ? 'border-blue-500 bg-blue-500' : isCurrent ? 'border-amber-400' : 'border-gray-300'}`}
      >
        {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
      </div>

      {/* 내용 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-semibold truncate ${isSelected ? 'text-blue-700' : isCurrent ? 'text-amber-700' : 'text-gray-800'}`}>
            {attrs.name || '장소'}
          </span>
          {isCurrent && (
            <span style={{ backgroundColor: '#f59e0b', color: '#ffffff', fontSize: '10px', padding: '2px 8px', borderRadius: '999px', fontWeight: '700', border: '1px solid #d97706', flexShrink: 0 }}>
              현재
            </span>
          )}
          {roleInfo && (
            <span style={{ backgroundColor: roleInfo.bg, color: roleInfo.color, fontSize: '10px', padding: '2px 8px', borderRadius: '999px', fontWeight: '700', flexShrink: 0 }}>
              {roleInfo.text}
            </span>
          )}
        </div>
        {(attrs.region || attrs.address) && (
          <div className={`text-xs mt-0.5 truncate ${isSelected ? 'text-blue-500' : isCurrent ? 'text-amber-500' : 'text-gray-400'}`}>
            {attrs.address || attrs.region}
          </div>
        )}
        {/* 이동시간 + 태그 */}
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          {displayTravelMinutes != null && (
            <span className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium border
              ${isSelected ? 'bg-blue-100 text-blue-600 border-blue-200' : 'bg-gray-100 text-gray-600 border-gray-300'}`}>
              <Clock size={9} />
              {selfTravelMinutes != null ? `내 이동 ${displayTravelMinutes}분` : `대중교통 최소 ${displayTravelMinutes}분`}
            </span>
          )}
          {attrs.tags?.slice(0, 2).map((tag, i) => (
            <span
              key={i}
              className={`text-[10px] px-1.5 py-0.5 rounded-full border
                ${isSelected ? 'bg-blue-100 text-blue-600 border-blue-200' : 'bg-gray-100 text-gray-600 border-gray-300'}`}
            >
              {tag}
            </span>
          ))}
        </div>
        {/* 수락확률/거절위험/노쇼위험 배지 (A4-03) */}
        <DGPredictionBadge predictions={candidate.dgPredictions} score={candidate.dgScore} />
      </div>

      {/* 우측: 평점 + 위치 버튼 (div로 중첩 button 방지) */}
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        {attrs.rating && (
          <span className="text-xs text-yellow-500 font-medium">★ {attrs.rating}</span>
        )}
        {hasLocation && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleLocationClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLocationClick(e); }}
            className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full border transition-colors cursor-pointer
              ${isSelected
                ? 'border-blue-300 text-blue-500 hover:bg-blue-100'
                : 'border-gray-200 text-gray-400 hover:bg-gray-100'
              }`}
          >
            <MapPin size={9} />
            위치
          </div>
        )}
      </div>
    </button>
  );
};

// ─── 참여자 카드 ─────────────────────────────────────────
const STATUS_BADGE = {
  accepted: { label: '참가',  bg: 'bg-green-100', text: 'text-green-600' },
  rejected: { label: '불참',  bg: 'bg-red-100',   text: 'text-red-500'  },
  pending:  { label: '대기중', bg: 'bg-gray-100',  text: 'text-gray-500' },
};

const PersonCard = ({ candidate, memberStatuses = {} }) => {
  const attrs = candidate.attributes || candidate;
  const status = memberStatuses[attrs.userId] || null;
  const badge = status ? STATUS_BADGE[status] : null;

  // 불참자: 텍스트 흐리게 처리
  const isRejected = status === 'rejected';

  return (
    <div className={`flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white border transition-colors
      ${isRejected ? 'border-red-100 opacity-60' : 'border-gray-100'}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
        ${isRejected ? 'bg-red-50' : 'bg-blue-100'}`}
      >
        <span className={`text-sm font-bold ${isRejected ? 'text-red-400' : 'text-blue-600'}`}>
          {(attrs.name || '?')[0]}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium truncate ${isRejected ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
          {attrs.name || '멤버'}
        </div>
      </div>
      {badge ? (
        <span className={`text-[10px] px-1.5 py-0.5 ${badge.bg} ${badge.text} rounded-full font-medium`}>
          {badge.label}
        </span>
      ) : attrs.role === 'required' && (
        <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded-full font-medium">
          필수
        </span>
      )}
    </div>
  );
};

// ─── 접을 수 있는 섹션 ────────────────────────────────────
const Section = ({ icon: Icon, title, count, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-1 mb-2"
      >
        <div className="flex items-center gap-1.5">
          <Icon size={14} className="text-blue-500" />
          <span className="text-sm font-bold text-gray-700">{title}</span>
          {count > 0 && (
            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-bold">
              {count}
            </span>
          )}
        </div>
        {open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>
      {open && <div className="space-y-1.5">{children}</div>}
    </div>
  );
};

// ─── 상태 요약 바 ─────────────────────────────────────────
const StatusBar = ({ engineState, lastAction }) => {
  if (!engineState) return null;
  const progress = Math.round((engineState.decisionProgress || 0) * 100);
  const purpose = engineState.purpose;
  const PInfo = purpose ? PURPOSE_MAP[purpose] : null;

  return (
    <div className="mb-4 px-1">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {PInfo && (
            <span className={`text-xs font-semibold ${PInfo.color}`}>
              <PInfo.icon size={11} className="inline mr-0.5" />
              {PInfo.label}
            </span>
          )}
          {lastAction?.type && (
            <span className="text-[11px] text-gray-400">
              · {ACTION_LABELS[lastAction.type] || lastAction.type}
            </span>
          )}
        </div>
        <span className="text-xs font-bold text-blue-600">{progress}%</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
        <div
          className="h-1.5 rounded-full bg-blue-500 transition-all duration-700"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// ─── 메인 패널 ────────────────────────────────────────────
const EngineCandidatesPanel = ({
  candidates,
  engineState,
  lastAction,
  isInitialized,
  isLoading,
  isModifyMode = false,
  memberStatuses = {},
  roomId,
  activeSuggestionId = null,
  suggRefreshKey = 0,
  onClose,
  onConfirm,
  dgStrategy,
}) => {
  const [selectedTime, setSelectedTime] = useState(null);   // null | 'undecided' | candidate
  const [selectedPlace, setSelectedPlace] = useState(null); // null | 'undecided' | candidate
  const [isConfirming, setIsConfirming] = useState(false);  // double-click 방지
  const [mapModal, setMapModal] = useState({ open: false, address: '', lat: null, lng: null });
  const [memberLocations, setMemberLocations] = useState([]);
  const [suggestionMembers, setSuggestionMembers] = useState([]); // 참석자 목록
  const [activeSuggestion, setActiveSuggestion] = useState(null); // 현재 제안 전체 데이터
  const [showDetailModal, setShowDetailModal] = useState(false);  // 일정 상세 모달

  const timeList  = candidates?.time   || [];
  const placeList = candidates?.place  || [];
  const personList = candidates?.person || [];

  // 패널 마운트 시: 기본 선택 후보 결정
  // - 같은 날짜 제안(시간 변경): 앵커(현재 확정) 카드 기본 선택
  // - 다른 날짜 새 일정 제안: 앵커 대신 첫 번째 실제 제안 후보 기본 선택
  //   (앵커 선택 채로 확정하면 기존 일정의 날짜·시간이 그대로 재생성되는 버그 방지)
  useEffect(() => {
    if (timeList.length > 0) {
      const currentTime = timeList.find(c => c.isCurrent);
      const firstNonAnchor = timeList.find(c => !c.isCurrent);
      const isDifferentDatePropose = !!(
        currentTime?.attributes?.date &&
        firstNonAnchor?.attributes?.date &&
        currentTime.attributes.date !== firstNonAnchor.attributes.date
      );
      setSelectedTime(isDifferentDatePropose ? firstNonAnchor : (currentTime || timeList[0]));
    }
    if (placeList.length > 0) {
      const currentPlace = placeList.find(c => c.isCurrent);
      setSelectedPlace(currentPlace || placeList[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 마운트 시 1회만

  // activeSuggestionId가 있을 때 참석자 목록 + 제안 전체 데이터 fetch
  useEffect(() => {
    if (!activeSuggestionId || !roomId) return;
    const fetchMembers = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/suggestions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const suggestions = await res.json();
        const target = suggestions.find(s => s._id === activeSuggestionId || s._id?.toString() === activeSuggestionId);
        if (target?.memberResponses) {
          setSuggestionMembers(target.memberResponses);
        }
        // 지도 보기용 전체 제안 데이터 저장
        if (target) {
          setActiveSuggestion(target);
        }
      } catch {
        // 조회 실패 시 무시
      }
    };
    fetchMembers();
  }, [activeSuggestionId, roomId, suggRefreshKey]);

  const hasAnyCandidates = timeList.length > 0 || placeList.length > 0 || personList.length > 0;

  const canConfirm = selectedTime !== null || selectedPlace !== null;

  const handleConfirm = () => {
    if (!canConfirm || isConfirming) return;
    setIsConfirming(true);
    onConfirm?.({
      time:  selectedTime  === 'undecided' ? null : selectedTime,
      place: selectedPlace === 'undecided' ? null : selectedPlace,
    });
    onClose?.();
  };

  const handleCancel = () => {
    setSelectedTime(null);
    setSelectedPlace(null);
    onClose?.();
  };

  // 지도 열기: 클릭 시 최신 위치 fetch 후 모달 오픈 (GPS 포함 → 카드 이동시간과 일치)
  const handleShowMap = async ({ address, lat: initLat, lng: initLng }) => {
    let resolvedLat = initLat;
    let resolvedLng = initLng;
    let locations = [];

    if (roomId) {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          // 좌표 없으면 서버 geocode로 먼저 획득 (MapModal이 useJsMap=true로 경로 계산하려면 필요)
          if (!resolvedLat || !resolvedLng) {
            try {
              const geoRes = await fetch(
                `${API_BASE_URL}/api/engine/geocode?place=${encodeURIComponent(address)}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              const geoData = await geoRes.json();
              if (geoData.success && geoData.lat && geoData.lng) {
                resolvedLat = geoData.lat;
                resolvedLng = geoData.lng;
              }
            } catch (_) {}
          }

          // 시간 후보 1순위에서 날짜 추출 (미래 약속이면 집주소 기준으로 경로 표시)
          const topDate = candidates?.time?.[0]?.attributes?.date || candidates?.time?.[0]?.date || null;

          // [5단계] suggestionId를 서버에 전달 → 서버가 isRejected 직접 반환
          const suggestionId = activeSuggestionId || null;
          let queryStr = topDate ? `?forDate=${topDate}` : '';
          if (suggestionId) {
            const sep = queryStr ? '&' : '?';
            queryStr += `${sep}suggestionId=${suggestionId}`;
          }

          // GPS 항상 수집: 집주소 미등록 시 서버 fallback으로 사용 (미래 약속 포함)
          try {
            await new Promise((resolve) => {
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  const sep = queryStr ? '&' : '?';
                  queryStr += `${sep}lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`;
                  resolve();
                },
                () => resolve(),
                { maximumAge: 60000, timeout: 2000 }
              );
            });
          } catch (_) {}

          const res = await fetch(`${API_BASE_URL}/api/engine/room-members/${roomId}${queryStr}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          if (data.success && Array.isArray(data.locations)) {
            // [5단계 수정] isRejected는 서버가 suggestionId 기반으로 직접 반환
            locations = data.locations;
            console.log('[EngineCandidatesPanel] suggestionId=' + suggestionId);
            console.log('[EngineCandidatesPanel] handleShowMap locations JSON:', JSON.stringify(locations.map(l => ({ name: l.name, uid: l.userId?.slice(-6), isRejected: l.isRejected }))));
          }
        }
      } catch (_) {}
    }
    setMemberLocations(locations);
    setMapModal({ open: true, address, lat: resolvedLat, lng: resolvedLng });
  };

  return (
    <>
      {/* 지도 모달: activeSuggestion 있으면 Google Maps(ScheduleDetailModal), 없으면 Leaflet 폴백 */}
      {mapModal.open && activeSuggestion && (
        <ScheduleDetailModal
          isOpen={mapModal.open}
          onClose={() => setMapModal({ open: false, address: '', lat: null, lng: null })}
          suggestion={activeSuggestion}
          roomId={roomId}
        />
      )}
      {mapModal.open && !activeSuggestion && (
        <MapModal
          address={mapModal.address}
          lat={mapModal.lat}
          lng={mapModal.lng}
          participantLocations={memberLocations}
          onClose={() => setMapModal({ open: false, address: '', lat: null, lng: null })}
        />
      )}

      {/* 일정 상세 모달 - 장소 클릭 시 열림 (지도 보기 버튼 제거됨) */}
      {showDetailModal && activeSuggestion && (
        <ScheduleDetailModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          suggestion={activeSuggestion}
          roomId={roomId}
        />
      )}

      {/* 백드롭 */}
      <div className="fixed inset-0 bg-black/30 z-[55]" onClick={handleCancel} />

      {/* 패널 */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-gray-50 rounded-t-2xl shadow-2xl z-[56]"
        style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* 핸들 */}
        <div className="flex justify-center pt-2.5 pb-1">
          <div className="w-9 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* 헤더 */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <CalendarPlus size={17} className="text-blue-500" />
            <h3 className="text-base font-bold text-gray-800">일정 조율</h3>
            {isLoading && (
              <div className="w-3.5 h-3.5 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
            )}
          </div>
          <button
            onClick={handleCancel}
            className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <X size={14} className="text-gray-500" />
          </button>
        </div>

        {/* 콘텐츠 */}
        <div className="flex-1 overflow-y-auto px-4 pt-3 pb-1">

          {/* 초기화 전 */}
          {!isInitialized && !isLoading && (
            <div className="text-center py-12 text-gray-400 text-sm">
              대화를 시작하면 자동으로 분석됩니다.
            </div>
          )}

          {/* 후보 없음 */}
          {isInitialized && !hasAnyCandidates && !isLoading && (
            <div className="text-center py-12 text-gray-400 text-sm">
              아직 추천 후보가 없어요.<br />
              시간이나 장소를 얘기해보세요.
            </div>
          )}

          {(isInitialized || isLoading) && hasAnyCandidates && (
            <>
              {/* 상태 요약 */}
              <StatusBar engineState={engineState} lastAction={lastAction} />

              {/* 시간 후보 */}
              {(timeList.length > 0) && (
                <Section icon={Clock} title="시간" count={timeList.length}>
                  {/* 미정 */}
                  <UndecidedCard
                    isSelected={selectedTime === 'undecided'}
                    onSelect={() => setSelectedTime(
                      selectedTime === 'undecided' ? null : 'undecided'
                    )}
                  />
                  {timeList.map((c, i) => (
                    <TimeCandidateCard
                      key={c.id || i}
                      candidate={c}
                      isSelected={selectedTime === c}
                      onSelect={() => setSelectedTime(selectedTime === c ? null : c)}
                    />
                  ))}
                </Section>
              )}

              {/* 장소 후보 */}
              {(placeList.length > 0) && (
                <Section icon={MapPin} title="장소" count={placeList.length}>
                  {/* 미정 */}
                  <UndecidedCard
                    isSelected={selectedPlace === 'undecided'}
                    onSelect={() => setSelectedPlace(
                      selectedPlace === 'undecided' ? null : 'undecided'
                    )}
                  />
                  {placeList.map((c, i) => (
                    <PlaceCandidateCard
                      key={`${c.id || c.attributes?.name || 'place'}-${i}`}
                      candidate={c}
                      isSelected={selectedPlace === c}
                      onSelect={() => setSelectedPlace(selectedPlace === c ? null : c)}
                      onShowMap={handleShowMap}
                    />
                  ))}
                </Section>
              )}

              {/* 참여자 */}
              {personList.length > 0 && (
                <Section icon={Users} title="참여자" count={personList.length} defaultOpen={false}>
                  {personList.map((c, i) => (
                    <PersonCard key={c.id || i} candidate={c} memberStatuses={memberStatuses} />
                  ))}
                </Section>
              )}
            </>
          )}
        </div>

        {/* 참석자 현황 (activeSuggestionId가 있을 때만 표시) */}
        {activeSuggestionId && suggestionMembers.length > 0 && (
          <div className="px-4 pt-2 pb-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Users size={14} className="text-gray-500" />
                <span className="text-xs font-semibold text-gray-500">참석자 현황</span>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              {suggestionMembers.map((r, i) => {
                const name = r.user
                  ? `${r.user.firstName || ''}${r.user.lastName || ''}`.trim() || r.user.email || '멤버'
                  : '멤버';
                const statusInfo = r.status === 'accepted'
                  ? { label: '참석', color: 'text-green-600', bg: 'bg-green-50' }
                  : r.status === 'rejected'
                  ? { label: r.isAutoRejected ? '자동 불참' : '불참', color: 'text-red-500', bg: 'bg-red-50' }
                  : { label: '대기 중', color: 'text-gray-400', bg: 'bg-gray-50' };
                return (
                  <div key={i} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-white border border-gray-100">
                    <span className="text-sm text-gray-700">{name}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusInfo.color} ${statusInfo.bg}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 하단 버튼 바 */}
        <div className="px-4 py-3 border-t border-gray-200 bg-white flex gap-2.5">
          {/* 일정 추가/수정 (왼쪽) */}
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-bold transition-all
              ${canConfirm
                ? 'bg-blue-500 text-white active:bg-blue-600 shadow-sm shadow-blue-200'
                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
              }`}
          >
            <Check size={15} strokeWidth={2.5} />
            {isModifyMode ? '일정 수정' : '일정 추가'}
          </button>

          {/* 취소 (오른쪽) */}
          <button
            onClick={handleCancel}
            className="flex-1 py-3 rounded-xl text-sm font-bold text-gray-500 bg-gray-100 hover:bg-gray-200 active:bg-gray-200 transition-all"
          >
            취소
          </button>
        </div>
      </div>
    </>
  );
};

export default EngineCandidatesPanel;
